// Agent's own dialer preferences, set from the Dialer settings popover.
//
// autoAnswer: agents.auto_pick_enabled. The admin sets the first value on agent create/edit;
// after that the agent changes it here through PATCH /v2/agents/me/auto-answer, which also
// writes the agent activity log. The initial value comes from the login / dashboard agent
// data; the switch updates it optimistically and rolls back if the save fails.
// The call itself is auto-answered from the SIP `Answer-Mode` header (softphone/demo-1.ts),
// which Asterisk sets from auto_pick_enabled.
import { create } from 'zustand';
import { patchApiCall } from '@/service/httpCall';
import { API_AGENT_AUTO_ANSWER } from '@/service/url';

interface DialerPrefsState {
  /** agentId -> on/off, seeded from the server value. */
  autoAnswerByAgent: Record<string, boolean>;
  saving: boolean;
  /** Seed from the server value (only until the agent changes it in this session). */
  hydrateAutoAnswer: (agentId: string | number, on: boolean) => void;
  setAutoAnswer: (agentId: string | number, on: boolean) => Promise<void>;
}

// Keep the cached login user in step so a page reload shows the saved value.
function patchStoredUser(on: boolean) {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem('user');
      if (!raw) continue;
      const user = JSON.parse(raw);
      if (!user?.agentData) continue;
      user.agentData.auto_pick_enabled = on;
      store.setItem('user', JSON.stringify(user));
    } catch {
      /* storage unavailable — the server value is still the source of truth */
    }
  }
}

export const useDialerPrefsStore = create<DialerPrefsState>()((set, get) => ({
  autoAnswerByAgent: {},
  saving: false,
  hydrateAutoAnswer: (agentId, on) =>
    set((s) =>
      String(agentId) in s.autoAnswerByAgent
        ? s
        : { autoAnswerByAgent: { ...s.autoAnswerByAgent, [String(agentId)]: on } },
    ),
  setAutoAnswer: async (agentId, on) => {
    const key = String(agentId);
    const prev = get().autoAnswerByAgent[key] ?? false;
    set((s) => ({ saving: true, autoAnswerByAgent: { ...s.autoAnswerByAgent, [key]: on } }));
    try {
      const resp: { auto_pick_enabled?: boolean } | undefined = await patchApiCall(API_AGENT_AUTO_ANSWER, {
        enabled: on,
      });
      const saved = typeof resp?.auto_pick_enabled === 'boolean' ? resp.auto_pick_enabled : on;
      set((s) => ({ autoAnswerByAgent: { ...s.autoAnswerByAgent, [key]: saved } }));
      patchStoredUser(saved);
    } catch {
      // httpCall already showed the error toast; put the switch back.
      set((s) => ({ autoAnswerByAgent: { ...s.autoAnswerByAgent, [key]: prev } }));
    } finally {
      set({ saving: false });
    }
  },
}));

/** The agent's auto-answer switch as currently known (false when unknown). */
export function useAgentAutoAnswer(agentId: string | number | null | undefined): boolean {
  return useDialerPrefsStore((s) => (agentId != null ? Boolean(s.autoAnswerByAgent[String(agentId)]) : false));
}
